import { TZDateMini } from '@date-fns/tz';
import {
  addDays,
  addMinutes,
  endOfDay,
  format,
  getMinutes,
  isAfter,
  isBefore,
  parse,
  roundToNearestMinutes,
  startOfDay,
} from 'date-fns';
import { path } from 'ramda';

import ooeConstants from '../constants';
import type { OrderDeliveryAddress } from '../order/schemas';
import { Holiday, HoursOfOperation, OperatingInterval, SpecialEvent } from '../types/location';
import { DaytimePhone, secondaryContactSchema, SecondaryContactErrors } from './schemas';

export const validateSecondaryContact = (values: {
  firstName: string;
  lastName: string;
  phoneNumber: string;
}): SecondaryContactErrors => {
  if (!values.firstName && !values.lastName && !values.phoneNumber) {
    return {};
  }

  const result = secondaryContactSchema.safeParse(values);
  if (result.success) {
    return {};
  }

  const errors: SecondaryContactErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0] as keyof SecondaryContactErrors;
    if (!errors[field]) {
      errors[field] = issue.message;
    }
  }
  return errors;
};

export const normalizeGuestCountField = (value: number | string): string => {
  let num: number;
  if (typeof value === 'string') {
    // reject empty strings or values that don't start with a digit.
    const trimmedValue = value.trim();
    if (!/^\d/.test(trimmedValue)) {
      return '';
    }
    num = parseInt(trimmedValue, 10);
  } else {
    num = value;
  }

  if (num > 9999) {
    return '9999';
  }
  if (num <= 0) {
    return '';
  }
  return String(num);
};

export const toDomesticDigits = (base?: string) => {
  const digits = (base ?? '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};

export const formatPhone = (phone?: DaytimePhone) => {
  const domesticNumber = toDomesticDigits(phone?.base);
  if (domesticNumber.length === 10) {
    return `(${domesticNumber.slice(0, 3)}) ${domesticNumber.slice(3, 6)}-${domesticNumber.slice(6)}`;
  }
  if (phone?.base) {
    return phone.base;
  }
  if (phone?.areaCode && phone?.phoneNumber) {
    return `${phone.areaCode}-${phone.phoneNumber}`;
  }
  return '';
};

export const validateDetails = (values: {
  time?: Nullable<string>;
  date?: Nullable<Date>;
  guestCount?: Nullable<string>;
}) => {
  const errors: { time?: string; date?: string; guestCount?: string } = {};
  if (!values.time) {
    errors.time = 'Required Field';
  }
  if (!values.date) {
    errors.date = 'Required Field';
  }
  if (values.guestCount && Number.isNaN(Number(values.guestCount))) {
    errors.guestCount = 'Please enter a number';
  }
  return errors;
};

export const isInBlackoutWindow = (
  slotTime: Date,
  endSlotTime: Date,
  blackoutStart: Date,
  blackoutMinutes: number,
) => {
  const blackoutEndTime = addMinutes(blackoutStart, blackoutMinutes);
  return isBefore(slotTime, blackoutEndTime) && isAfter(endSlotTime, blackoutStart);
};

export const getOperatingIntervalTimeslots = ({
  operatingInterval,
  blackoutHours,
  timeSlotStartDate,
}: {
  operatingInterval: OperatingInterval;
  blackoutHours?: OperatingInterval;
  timeSlotStartDate: Date;
}) => {
  const { openTime, durationInMinutes } = operatingInterval;
  const parsedOpenTime = parse(openTime, ooeConstants.DATE_TIME_FORMAT.time, timeSlotStartDate);
  const endTime = addMinutes(parsedOpenTime, durationInMinutes);
  const earliestAvailableTime = format(timeSlotStartDate, ooeConstants.DATE_TIME_FORMAT.time);
  return getTimeSlots({
    openTime: earliestAvailableTime > openTime ? timeSlotStartDate : parsedOpenTime,
    endTime,
    blackoutStart: blackoutHours
      ? parse(blackoutHours.openTime, ooeConstants.DATE_TIME_FORMAT.time, timeSlotStartDate)
      : undefined,
    blackoutMinutes: blackoutHours?.durationInMinutes,
  });
};

export const getHolidayTimeslots = ({
  holiday,
  timeSlotStartDate,
}: {
  holiday: Holiday;
  timeSlotStartDate: Date;
}) => {
  const { holidayHours } = holiday;
  const { operationType: holidayOperationType, operatingInterval: holidayOperatingInterval } = holidayHours;

  if (!holidayOperatingInterval || holidayOperationType === 'closed') {
    return [];
  }

  return getOperatingIntervalTimeslots({
    operatingInterval: holidayOperatingInterval,
    timeSlotStartDate,
  });
};

export const dateIsSpecialEvent = (date: Date) => (specialEvent: SpecialEvent) => {
  const sd = parse(specialEvent.startDate, ooeConstants.DATE_TIME_FORMAT.date, date);
  const ed = parse(specialEvent.endDate, ooeConstants.DATE_TIME_FORMAT.date, date);
  const isSpecialEvent = !isAfter(sd, date) && !isBefore(ed, date);
  return !!isSpecialEvent;
};

export const getSpecialEventTimeslots = ({
  specialEvent,
  timeSlotStartDate,
}: {
  specialEvent: SpecialEvent;
  timeSlotStartDate: Date;
}) => {
  const { operationType: specialOperationType, operatingInterval: specialOperatingInterval } =
    specialEvent.specialEventHours;

  if (!specialOperatingInterval || specialOperationType !== 'standardHours') {
    return [];
  }

  return getOperatingIntervalTimeslots({
    operatingInterval: specialOperatingInterval,
    timeSlotStartDate,
  });
};

export const getEarliestAvailabeDate = ({
  leadTimeMinutes = 0,
  timeZone,
}: {
  leadTimeMinutes?: number;
  timeZone: string;
}) => {
  const now = TZDateMini.tz(timeZone);
  const leadTimeDate = addMinutes(now, leadTimeMinutes);
  return roundToNearestMinutes(leadTimeDate, {
    nearestTo: ooeConstants.TIME_SLOT_INTERVAL_IN_MIN,
    roundingMethod: 'ceil',
  });
};

export const generateRestrictedTimeSlots = ({
  hoursOfOperation,
  specialEvents = [],
  pickedDate,
  leadTimeMinutes,
  timeZone,
}: {
  hoursOfOperation: HoursOfOperation;
  specialEvents?: SpecialEvent[];
  pickedDate: Date;
  leadTimeMinutes?: number;
  timeZone: string;
}) => {
  const earliestAvailableDate = getEarliestAvailabeDate({
    leadTimeMinutes,
    timeZone,
  });
  const formattedPickedDate = format(pickedDate, ooeConstants.DATE_TIME_FORMAT.date);
  const formattedEarliestAvailableDate = format(earliestAvailableDate, ooeConstants.DATE_TIME_FORMAT.date);

  if (formattedPickedDate < formattedEarliestAvailableDate) {
    return [];
  }

  const dayOfWeek = format(pickedDate, 'EEEE').toLocaleLowerCase() as Exclude<
    keyof HoursOfOperation,
    'holidays'
  >;
  const operationType = path<string>([dayOfWeek, 'operationType'], hoursOfOperation);
  const operatingInterval = path<OperatingInterval>([dayOfWeek, 'operatingInterval'], hoursOfOperation);
  const { holidays } = hoursOfOperation;
  const holiday = holidays?.find((day) => day.holidayDate === formattedPickedDate);
  const specialEvent = specialEvents.find(dateIsSpecialEvent(pickedDate));
  const startOfPickedDate = startOfDay(pickedDate);
  const timeSlotStartDate =
    formattedPickedDate > formattedEarliestAvailableDate ? startOfPickedDate : earliestAvailableDate;

  let timeSlots: string[] = [];

  if (holiday) {
    timeSlots = getHolidayTimeslots({
      holiday,
      timeSlotStartDate,
    });
  } else if (specialEvent) {
    timeSlots = getSpecialEventTimeslots({
      specialEvent: specialEvent,
      timeSlotStartDate,
    });
  } else if (operationType === 'standardHours' && operatingInterval) {
    const blackoutHours = hoursOfOperation[dayOfWeek]?.blackoutHours;
    timeSlots = getOperatingIntervalTimeslots({
      operatingInterval,
      blackoutHours,
      timeSlotStartDate,
    });
  } else if (operationType === 'open24Hours') {
    timeSlots = getTimeSlots({
      openTime: timeSlotStartDate,
      endTime: parse('23:45', ooeConstants.DATE_TIME_FORMAT.time, pickedDate),
    });
  }

  return timeSlots.map((t) => ({ time: t, available: true }));
};

export const getTimeSlots = ({
  openTime,
  endTime,
  blackoutStart = parse('00:00', ooeConstants.DATE_TIME_FORMAT.time, openTime),
  blackoutMinutes = 0,
}: {
  openTime: Date;
  endTime: Date;
  blackoutStart?: Date;
  blackoutMinutes?: number;
}) => {
  const timeSlotsArr = [];
  let currentSlotOpenTime = openTime;
  while (!isAfter(currentSlotOpenTime, endTime)) {
    const currentSlotEndTime = addMinutes(currentSlotOpenTime, ooeConstants.TIME_SLOT_INTERVAL_IN_MIN);
    if (!isInBlackoutWindow(currentSlotOpenTime, currentSlotEndTime, blackoutStart, blackoutMinutes)) {
      timeSlotsArr.push(format(currentSlotOpenTime, ooeConstants.DATE_TIME_FORMAT.time));
    }
    currentSlotOpenTime = addMinutes(currentSlotOpenTime, ooeConstants.TIME_SLOT_INTERVAL_IN_MIN);
  }
  return timeSlotsArr;
};

export const generateTimeSlots = () => {
  let startTime = startOfDay(new Date());
  const endTime = endOfDay(new Date());

  const timeSlots = [];
  while (startTime <= endTime) {
    timeSlots.push({
      available: true,
      time: format(startTime, ooeConstants.DATE_TIME_FORMAT.time),
    });
    startTime = addMinutes(startTime, ooeConstants.TIME_SLOT_INTERVAL_IN_MIN);
  }
  return timeSlots;
};

export const generateTimeSlotsForToday = (timeZone: string) => {
  const now = TZDateMini.tz(timeZone);
  const remainder =
    ooeConstants.TIME_SLOT_INTERVAL_IN_MIN - (getMinutes(now) % ooeConstants.TIME_SLOT_INTERVAL_IN_MIN);
  let startTime = addMinutes(now, remainder);
  const endTime = endOfDay(now);

  const timeSlots = [];
  while (startTime <= endTime) {
    timeSlots.push({
      available: true,
      time: format(startTime, ooeConstants.DATE_TIME_FORMAT.time),
    });
    startTime = addMinutes(startTime, ooeConstants.TIME_SLOT_INTERVAL_IN_MIN);
  }
  return timeSlots;
};

export const isDateBeforeRestaurantToday = (date: Date, timeZone: string) => {
  const formattedDate = format(date, ooeConstants.DATE_TIME_FORMAT.date);
  const restaurantToday = format(TZDateMini.tz(timeZone), ooeConstants.DATE_TIME_FORMAT.date);
  return formattedDate < restaurantToday;
};

export const isValidOrderDate = ({
  date,
  availableDates,
  bypassBusinessRules,
  timeZone,
}: {
  date: Date;
  availableDates: string[];
  bypassBusinessRules: boolean;
  timeZone: string;
}) => {
  const formattedDate = format(date, ooeConstants.DATE_TIME_FORMAT.date);
  const startDate = TZDateMini.tz(timeZone);
  const endDate = addDays(startDate, 500);
  const formattedEndDate = format(endDate, ooeConstants.DATE_TIME_FORMAT.date);
  const inDateRange = !isDateBeforeRestaurantToday(date, timeZone) && formattedDate <= formattedEndDate;

  if (bypassBusinessRules) {
    return inDateRange;
  }

  return inDateRange && availableDates.includes(formattedDate);
};

export const getAddressSearchSeed = ({ addressLine1, city, state, zip }: OrderDeliveryAddress) => {
  const stateZip = [state, zip].filter(Boolean).join(' ');
  const secondaryText = [city, stateZip].filter(Boolean).join(', ');
  const searchValue = [addressLine1, city, stateZip].filter(Boolean).join(', ');
  // Keep Unit (addressLine2) out of the Google query — it is applied separately via
  // subpremise. Including it often yields no autocomplete results on edit/reorder.
  const addressToValidate = `${addressLine1} ${city} ${state}`;

  return { mainText: addressLine1 ?? '', secondaryText, searchValue, addressToValidate };
};
